import { Link } from "react-router-dom";
import { Clock, BookMarked } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "./ui/badge";

const DOMAIN_COLORS = {
  BHV: "bg-purple-100 text-purple-800",
  FND: "bg-blue-100 text-blue-800",
  O2O: "bg-pink-100 text-pink-800",
  INF: "bg-indigo-100 text-indigo-800",
  REC: "bg-emerald-100 text-emerald-800",
  INV: "bg-amber-100 text-amber-800",
  CRO: "bg-rose-100 text-rose-800",
  PRE: "bg-cyan-100 text-cyan-800",
  TEM: "bg-violet-100 text-violet-800",
};

const FORMAT_LABEL_FR = { C: "Court", L: "Long", D: "Dédié" };
const FORMAT_LABEL_EN = { C: "Short", L: "Long", D: "Dedicated" };

/**
 * Workshop card used in library, prescriptions list, etc. The title language
 * follows the UI locale by default (library), but can be overridden via
 * `titleLocale` prop when rendered inside a diagnostic (group language).
 */
export default function AtelierCard({ atelier, testId, compact = false, titleLocale }) {
  const { i18n } = useTranslation();
  const uiLocale = (i18n.language || "fr").startsWith("en") ? "en" : "fr";
  const titleLoc = titleLocale === "en" || (titleLocale !== "fr" && uiLocale === "en") ? "en" : "fr";
  const title = titleLoc === "en"
    ? (atelier.title_en || atelier.title_fr || atelier.id)
    : (atelier.title_fr || atelier.title_en || atelier.id);
  const formats = uiLocale === "en" ? FORMAT_LABEL_EN : FORMAT_LABEL_FR;
  return (
    <Link
      to={`/atelier/${atelier.id}`}
      data-testid={testId || `atelier-card-${atelier.id}`}
      className="group block bg-white border border-border rounded-xl p-4 md:p-5 transition-all hover:shadow-md hover:-translate-y-0.5 hover:border-apollo-orange/40"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <span className="font-mono text-xs px-2 py-0.5 rounded bg-bg-soft border border-border text-text-secondary" data-testid={`atelier-id-${atelier.id}`}>
          {atelier.id}
        </span>
        <Badge className={`${DOMAIN_COLORS[atelier.domain] || "bg-gray-100 text-gray-800"} border-0`} data-testid={`atelier-domain-${atelier.id}`}>
          {atelier.domain}
        </Badge>
      </div>
      <h3 className="font-heading font-bold text-base md:text-lg leading-snug text-text-primary group-hover:text-apollo-orange-dark transition-colors">
        {title}
      </h3>
      {!compact && atelier.description && (
        <p className="text-sm text-text-secondary mt-2 line-clamp-2">{atelier.description}</p>
      )}
      <div className="flex items-center gap-3 mt-3 text-xs text-text-secondary">
        <span className="inline-flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {atelier.duree_minutes}min
        </span>
        <span className="inline-flex items-center gap-1">
          <BookMarked className="w-3 h-3" />
          {formats[atelier.format]}
        </span>
        <span>·</span>
        <span className="font-medium">{atelier.niveau_animateur}</span>
      </div>
      {!compact && atelier.symptomes_traites?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-3">
          {atelier.symptomes_traites.slice(0, 3).map((s) => (
            <span key={s} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-apollo-orange-light text-apollo-orange-dark">
              {s}
            </span>
          ))}
        </div>
      )}
    </Link>
  );
}
